/**
 * ============================================================================
 * WBR AGENT CHAT WIDGET — the embedded QuickSight chat agent entry point.
 * ============================================================================
 *
 * The docked panel embedding the WBR Agent's QuickSight chat experience. The
 * launcher lives in the page header controls row (WbrAgentLauncher, beside the
 * Live/Snapshot toggle) and opens this panel via the `chatAgentOpen` store
 * flag. The signed embed URL and the agent's per-stage QuickSight quick id come
 * from the service's GenerateEmbedUrlForChatAgent API (features/api/chatAgentApi).
 *
 * WHY THE SDK, NOT A RAW IFRAME: a bare `<iframe src={chatEmbedUrl}>` loads
 * QuickSight's quick-chat experience, which opens the caller's DEFAULT personal
 * assistant, not the WBR agent. To pin it to the WBR agent we embed through the
 * QuickSight Embedding SDK (`createEmbeddingContext().embedQuickChat`) and pass
 * the agent's quick id as `agentOptions.fixedAgentId`. `fixedAgentId` is exactly
 * the `agentQuickId` the API returns. (Precedent: ADSProjectPortalUI CTSChatAgent,
 * ACTShellUIAssets QuickChatPanel.)
 *
 * VISIBILITY is gated on the `Feature.CHAT_AGENT` entry in the GetPermissions
 * profile (fail-closed via selectFeatureAccessible): the widget renders nothing
 * unless the caller's profile confirms access, mirroring the server, which gates
 * the embed API on the same WBR_CALLOUTS lock.
 *
 * The embed URL is fetched LAZILY on first open (the query is skipped until
 * then) because it is a time-limited bearer credential. The SDK injects its
 * iframe into a container that stays mounted (hidden, not unmounted, when the
 * panel collapses), so the opened session survives collapse/expand — and the
 * embed runs at most once (embeddedRef guard). A failure surfaces a retry that
 * re-fetches a fresh URL and re-embeds.
 */

import React, { useEffect, useRef, useState } from 'react';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Spinner from '@amzn/awsui-components-core/spinner';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import { chatAgentClosed } from 'src/features/dashboard/dashboardSlice';
import {
  useGetPermissionsQuery,
  selectFeatureAccessible,
} from 'src/features/api/permissionsApi';
import { useGetChatAgentEmbedUrlQuery } from 'src/features/api/chatAgentApi';
import { CHAT_AGENT_FEATURE_ID, WBR_AGENT } from 'src/types/chatAgentApi.types';
import './WbrAgentChatWidget.scss';

const PANEL_TITLE = 'WBR Agent';

export const WbrAgentChatWidget: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: permissions } = useGetPermissionsQuery();
  const accessible = selectFeatureAccessible(
    permissions,
    CHAT_AGENT_FEATURE_ID,
  );

  // Open state lives in the store: the launcher (WbrAgentLauncher, in the page
  // header controls) opens it; this app-level panel renders it.
  const open = useAppSelector((s) => s.dashboard.chatAgentOpen);
  // Lazily mount the embed query only after the first open: the URL is a
  // time-limited credential, so we don't fetch it until the user asks.
  const [launched, setLaunched] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Guards against embedding twice — the container is never unmounted, so a
  // second embed would stack a duplicate iframe.
  const embeddedRef = useRef(false);
  // True once embedQuickChat resolves; drives the loading overlay. A boolean
  // "embedded" flag (rather than a transient "embedding" flag gated on the
  // effect's cleanup) is deliberate: React StrictMode double-invokes the effect
  // and fires the first cleanup even though the component stays mounted, so a
  // cleanup-gated success update would strand the overlay in dev.
  const [embedded, setEmbedded] = useState(false);
  const [embedFailed, setEmbedFailed] = useState(false);

  const {
    data: embed,
    isFetching,
    isError,
    refetch,
  } = useGetChatAgentEmbedUrlQuery(WBR_AGENT, { skip: !launched });

  // Embed once the panel is open and the signed URL + quick id are in hand.
  // Passing the quick id as fixedAgentId is what opens the WBR agent instead of
  // the caller's default assistant. `launched` is a dep because the container
  // only mounts once launched flips true — one render AFTER open — so the
  // effect must re-run then to find the container.
  useEffect(() => {
    if (
      !open ||
      !launched ||
      isFetching ||
      !embed ||
      !containerRef.current ||
      embeddedRef.current
    ) {
      return;
    }
    embeddedRef.current = true;
    setEmbedFailed(false);
    const container = containerRef.current;
    // Clear any leftover iframe from a prior failed attempt before re-embedding.
    container.innerHTML = '';
    void (async () => {
      try {
        const context = await createEmbeddingContext();
        await context.embedQuickChat(
          {
            url: embed.chatEmbedUrl,
            container,
            height: '100%',
            width: '100%',
          },
          { agentOptions: { fixedAgentId: embed.agentQuickId } },
        );
        setEmbedded(true);
      } catch {
        embeddedRef.current = false; // allow a retry to re-embed
        setEmbedFailed(true);
      }
    })();
  }, [open, launched, embed, isFetching]);

  // On open: mount the embed query, and if a prior attempt failed re-fetch a
  // fresh (short-lived) URL so reopening recovers instead of showing the stale
  // error. Runs on the open transition since the launcher now lives elsewhere.
  useEffect(() => {
    if (!open) return;
    setLaunched(true);
    if (embedFailed) {
      embeddedRef.current = false;
      setEmbedded(false);
      setEmbedFailed(false);
      void refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Gated feature: render nothing unless the profile confirms access.
  if (!accessible) {
    return null;
  }

  // The signed URL is short-lived, so retry re-fetches a fresh one; the effect
  // re-embeds when it arrives (guard + embedded flag reset first).
  const retry = () => {
    embeddedRef.current = false;
    setEmbedded(false);
    setEmbedFailed(false);
    void refetch();
  };

  // While a (re)fetch is in flight, prefer the loading overlay over the error
  // screen — RTK keeps isError true through a refetch, so without this a Retry
  // would leave the error frozen with no loading feedback.
  const showError = (isError || embedFailed) && !isFetching;
  // Show the overlay from open until the embed resolves — covers the fetch, the
  // async embed, and the frame before the effect runs (no blank flash).
  const showLoading = !showError && !embedded;

  return (
    <>
      {/* Mounted once launched; hidden (not unmounted) when collapsed so the
          opened QuickSight session survives a collapse/expand. */}
      {launched && (
        <div
          className={`wbr-chat__panel${open ? ' wbr-chat__panel--open' : ''}`}
          role="complementary"
          aria-label={PANEL_TITLE}
          aria-hidden={!open}
        >
          <div className="wbr-chat__header">
            <span className="wbr-chat__title">{PANEL_TITLE}</span>
            <Button
              variant="icon"
              iconName="close"
              ariaLabel="Close WBR Agent"
              onClick={() => dispatch(chatAgentClosed())}
            />
          </div>

          <div className="wbr-chat__body">
            {/* The QuickSight SDK injects its iframe into this container. It is
                always mounted (once launched) so the embed runs once and the
                session persists; overlays cover it while loading or on error. */}
            <div ref={containerRef} className="wbr-chat__embed" />

            {showLoading && (
              <div className="wbr-chat__overlay">
                <Box textAlign="center">
                  <Spinner /> Loading WBR Agent…
                </Box>
              </div>
            )}

            {showError && (
              <div className="wbr-chat__overlay">
                <SpaceBetween direction="vertical" size="s">
                  <Box color="text-status-error">
                    Couldn't load the WBR Agent. Please try again.
                  </Box>
                  <Button iconName="refresh" onClick={retry}>
                    Retry
                  </Button>
                </SpaceBetween>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
